import React from 'react'
import { Autocomplete } from '@material-ui/lab'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { Checkbox, CircularProgress, TextField } from '@material-ui/core'
import PropTypes from 'prop-types'

const AutoCompleteField = props => {
  const {
    options = [],
    labelKey = 'name',
    labelKeys = [],
    label = '',
    placeholder = '',
    value: propValue,
    valueKey = '',
    valueKeys = [],
    name = '',
    onChange = () => {},
    fullWidth = false,
    disabled = false,
    multiple = false,
    loading = false,
    error = false,
    helperText = '',
    ...other
  } = props

  const value = multiple ? (Array.isArray(propValue) ? propValue : propValue ? [propValue] : []) : propValue || null

  const getValue = () => {
    if (valueKey && valueKeys?.length > 0) {
      throw new Error('valueKey and valueKeys are mutually exclusive')
    }

    if (multiple) {
      if (valueKey) {
        return options?.filter(item => value.includes(item[valueKey])) || []
      }
      if (valueKeys?.length > 0) {
        const key = valueKeys[0]
        return options?.filter(item => value.some(vl => vl[key] === item[key])) || []
      }
      return []
    } else {
      if (valueKey) {
        return options?.find(item => item[valueKey] === value) || null
      }
      if (valueKeys?.length > 0 && value && typeof value === 'object') {
        const key = valueKeys[0]
        return options?.find(item => item[key] === value[key]) || null
      }
      return value || null
    }
  }

  const getOptionLabel = option => {
    if (labelKeys?.length > 0 && !!labelKey && labelKey !== 'name') {
      throw new Error('labelKey and labelKeys are mutually exclusive')
    }

    if (labelKeys?.length > 0) {
      return labelKeys?.map(key => option[key]).join(' ') || ''
    }
    return option[labelKey] || ''
  }

  const handleChange = (event, newValue) => {
    let updatedValue

    if (valueKey) {
      if (multiple) {
        updatedValue = newValue.map(item => item?.[valueKey])
      } else {
        updatedValue = newValue?.[valueKey] || null
      }
    } else if (valueKeys?.length > 0) {
      if (multiple) {
        updatedValue = newValue.map(item => {
          const obj = {}
          valueKeys.forEach(key => {
            obj[key] = item?.[key] || null
          })
          return obj
        })
      } else {
        updatedValue = {}
        valueKeys.forEach(key => {
          updatedValue[key] = newValue?.[key] || null
        })
      }
    } else {
      updatedValue = newValue
    }

    onChange({
      target: {
        name,
        value: updatedValue
      }
    })
  }

  const renderTags = (value, getTagProps) => {
    if (!value || value.length === 0) {
      return null
    }

    return (
      <>
        {value[0] && (
          <span style={{ paddingLeft: 10 }} {...getTagProps({ index: 0 })}>
            {value[0][labelKey]}
          </span>
        )}
        {value.length > 1 && (
          <span style={{ paddingLeft: 10 }} {...getTagProps({ index: 1 })}>
            +{value.length - 1}
          </span>
        )}
      </>
    )
  }

  return (
    <Autocomplete
      options={options || []}
      disabled={disabled}
      multiple={multiple}
      disableCloseOnSelect={multiple}
      getOptionLabel={getOptionLabel}
      value={getValue()}
      onChange={handleChange}
      popupIcon={<ExpandMoreIcon style={{ margin: '0 8px 0 8px' }} />}
      loading={loading}
      {...(multiple
        ? {
            limitTags: -2,
            renderTags,
            renderOption: (option, { selected }) => (
              <React.Fragment>
                <Checkbox color="primary" style={{ marginRight: 2 }} checked={selected} />
                {getOptionLabel(option)}
              </React.Fragment>
            )
          }
        : {})}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth={fullWidth}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            ...other.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="primary" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            )
          }}
          InputLabelProps={{
            shrink: true
          }}
        />
      )}
      {...other}
    />
  )
}

AutoCompleteField.propTypes = {
  options: PropTypes.array,
  keyArr: PropTypes.string,
  labelKey: PropTypes.string,
  labelKeys: PropTypes.array,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  limitTags: PropTypes.number,
  value: PropTypes.any,
  valueKey: PropTypes.string,
  valueKeys: PropTypes.array,
  name: PropTypes.string,
  onChange: PropTypes.func,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  isMobile: PropTypes.bool,
  multiple: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string
}

AutoCompleteField.defaultProps = {
  options: [],
  keyArr: 'id',
  labelKey: 'name',
  labelKeys: [],
  label: '',
  placeholder: '',
  limitTags: 1,
  value: null,
  valueKey: '',
  valueKeys: [],
  name: '',
  onChange: () => {},
  fullWidth: false,
  disabled: false,
  isMobile: false,
  multiple: false,
  loading: false,
  error: false,
  helperText: ''
}

export default AutoCompleteField
